import '../App.css'
import { useState, useRef, useContext, useEffect, useMemo } from 'react';
import TableTabs from './Tabs/TableTabs.js';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import HomeIcon from '@mui/icons-material/Home';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import NativeSelect from '@mui/material/NativeSelect';
import OutlinedInput from '@mui/material/OutlinedInput';
import { LightDarkModeContext } from '../context/LightDarkModeContext.js';
import RoadsMap from './RoadsMap.js';
import useSWR from 'swr';
import { FOCUS_COLS, ROAD_LABELS, ROAD_LABELS_NAMES, REP_DISTRICTS, fetcher, CONG_DISTRICTS, GEOGRAPHIES_DEFAULT } from '../constants.js';
import CircularProgress from '@mui/material/CircularProgress';
import TutorialPopper from './TutorialPopper.js';
import FiltersOptions from './FiltersOptions.js';
import { useLocation } from 'react-router-dom';
import useSWRInfinite from 'swr/infinite';
// from MUI 'Mini variant drawer' example (https://mui.com/material-ui/react-drawer/#mini-variant-drawer)

// NativeSelect is used for a selection, and provides a better experience on mobile devices.
// (https://mui.com/material-ui/react-select/#native-select)
// OutlinedInput is used to display the selection with a surrounding solid border.

// When selections were not displaying properly, these sources helped:
// https://github.com/mui/material-ui/issues/32197
// https://github.com/mui/material-ui/issues/32301
// https://mui.com/material-ui/api/native-select/

const categoryMap = new Map([
  ['counties', 'County'],
  ['districts', 'District'],
  ['mpos', 'Mpo'],
  ['cong', 'Cong'],
  ['rep', 'Rep'],
  ['regions', 'Region'],
])

const TOTAL_WIDTH = 100

function convertYearsToArray(yearsData) {
  let y = []
  if (yearsData) {
    for (let i = yearsData.min; i <= yearsData.max; i++) {
      y.push(i)
    }
    return y
  } else {
    return []
  }
}

const filterMapData = (labelFilters, mapData) => {
  let newObj = {}
  for (const l of ROAD_LABELS) {
    newObj[l.categoryKey] = []
  }
  for (const labelFilter of labelFilters) {
    const parts = labelFilter.split('-')
    if (parts.length !== 2) {
      console.error('label filters deconstructed failed')
      return null
    }
    newObj[parts[0]].push(parseInt(parts[1]))
  }

  if (Array.isArray(mapData)) {
    let filteredData = []
    mapData.forEach(mapChunk => {
      let filteredChunk = mapChunk
      let i = 0
      for (const [key, val] of Object.entries(newObj)) {
        if (val.length === 0) {
          return []
        } else if (val.length < Object.keys(ROAD_LABELS[i].labels).length) {
          console.log(`Filter with ${key}`)
          console.log(val)
          if (key === 'urban' && val.length === 1 && val[0] === 1) {
            filteredChunk = filteredChunk.filter(d => d[key] !== 0)
          } else {
            filteredChunk = filteredChunk.filter(d => val.includes(d[key]))
          }
        }
        i += 1
      }
      filteredData.push(...filteredChunk)
    });
    return filteredData
  } else {
    return null
  }
}

export default function TableAndMapModule() {
  const theme = useTheme();
  const colorMode = useContext(LightDarkModeContext);
  const { pathname } = useLocation() // get URL string after the slash (/) (either '/roads' or '/mpo')

  // Fetch the layer names from the API
  const { data: layerNames, error: layerNamesError } = useSWR('/getGeographies', fetcher, {
    fallbackData: GEOGRAPHIES_DEFAULT,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  // Fetch the years from the API
  const { data: years, error: yearsError } = useSWR('/years', fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  })
  const mapRef = useRef(null);
  const geoLayerRef = useRef(null);
  const containerRef = useRef(null);
  const resizeRef = useRef(null);
  const [tableTabValue, setTableTabValue] = useState(0); // set table tab to 'summary' by default
  const [fromToTable, setFromToTable] = useState([]);
  const [showOnMapData, setShowOnMapData] = useState([]);
  const [showOnMapMonitoredData, setShowOnMapMonitoredData] = useState([]);
  const [mapFocus, setMapFocus] = useState('None')
  const [category, setCategory] = useState(pathname === '/mpo' ? 'mpos' : 'None')
  const [layer, setLayer] = useState('None')
  const [year, setYear] = useState('2020')
  const [openTutorial, setOpenTutorial] = useState(false)
  const [labelFilters, setLabelFilters] = useState(ROAD_LABELS_NAMES)
  const [shouldFitMapLayerBounds, setShouldFitMapLayerBounds] = useState(false)

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })
  const [mapPagination, setMapPagination] = useState({
    pageIndex: 0,
    pageSize: 20000
  })
  
  const urlKey = (pageIndex) => {
    if (category === 'State' && tableTabValue !== 2) {
      return `/state?year=${year}&limit=${pagination.pageSize}&offset=${(pagination.pageIndex * pagination.pageSize)}`
    } else if (Array.from(categoryMap.keys()).includes(category) && layer !== 'None') {
      return `/getRoadby${categoryMap.get(category)}WithLimit?year=${year}&${categoryMap.get(category).toLowerCase()}=${layer}&limit=${mapPagination.pageSize}&offset=${pageIndex * mapPagination.pageSize}`
    } else {
      return null
    }
  }

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { data: mapData, isLoading, error: mapDataError, setSize: setChunks } = useSWRInfinite(urlKey, fetcher, {
    shouldRetryOnError: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateFirstPage: false,
  })
  const filteredMapData = useMemo(() => filterMapData(labelFilters, mapData), [labelFilters, mapData])
  
  const [mapWidth, setMapWidth] = useState(50)
  const [isResizing, setIsResizing] = useState(false)

  const yearsArray = convertYearsToArray(years)

  const handleMouseDown = (event) => {
    event.preventDefault()
    setIsResizing(true)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleMouseMove = (event) => {
    resizeRef.current.setAttribute('style', `position:absolute; left:${event.clientX}px; background-color:white; opacity:0.5; width:1vw; height:80vh; z-index:1000`)
  }

  const handleMouseUp = (event) => {
    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const newMapWidth = ((containerRect.right - event.clientX) / viewportWidth) * 100;

    if (newMapWidth >= 10 && newMapWidth <= TOTAL_WIDTH - 10) {
      setMapWidth(newMapWidth);
    }
    setIsResizing(false)
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  // Resize map after moving the resize handle
  useEffect(() => { 
    if (mapRef.current !== null && !isResizing) {
      mapRef.current.resize()
    }
  }, [isResizing])

  // useEffect(() => {
  //   if (mapData && mapData.length > 0) {
  //     setAllMapData(prevData => [...prevData, ...mapData]);
  //   }
  // }, [mapData]);

  useEffect(() => { // runs every time a chunk is successfully loaded.
    if (Array.isArray(mapData?.at(-1)) && mapData.at(-1).length === mapPagination.pageSize) {
      // isLoadingMore state is only active after first chunk is loaded
      setIsLoadingMore(true)
      // increase number of chunks if there is a successful mapData load and current chunk is equal to chunk size
      setChunks(prev => prev+1)
    } else if (Array.isArray(mapData?.at(-1)) && mapData.at(-1).length < mapPagination.pageSize) { // last chunk
      setShouldFitMapLayerBounds(true)
      setIsLoadingMore(false)
    }
  }, [mapData, setChunks]);


  // useEffect(() => {
  //   if (mapData && mapData.length < mapPagination.pageSize) {
  //     setIsLoading(false); 
  //   }
  // }, [mapData]);

  return (
    <div>
      {(isLoading || isLoadingMore) &&
        <Stack style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgb(0, 0, 0, 50%)', position: 'absolute', zIndex: 3, width: '100vw', height: '90vh' }}>
          <div style={{ fontSize: '3rem' }}>Loading Data... </div>
          <CircularProgress size='5rem' />
        </Stack>
      }
      <Stack id='roads-options' className='options-stack' direction='row' spacing={1}>
        <div id='icon-buttons' style={{ display: 'flex' }}>
          <Tooltip title='Home' placement='top'>
            <IconButton aria-label='home' href='/'>
              <HomeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Switch to ${theme.palette.mode === 'light' ? 'dark' : 'light'} mode`} placement='top'>
            <IconButton aria-label='light and dark mode switch' onClick={colorMode.toggleColorMode}>
              {theme.palette.mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title='Show tutorial' placement='top'>
            <IconButton aria-label='show tutorial' onClick={() => { setOpenTutorial(true) }}>
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </div>
        { pathname === '/roads' &&
          <FormControl fullWidth>
            <InputLabel htmlFor="select-category">
              Category:
            </InputLabel>
            <NativeSelect
              className='roads-selection'
              input={<OutlinedInput label="select-category" />}
              value={category}
              onChange={(e) => { setCategory(e.target.value); setLayer('None'); }}
              inputProps={{
                name: 'category',
                id: "select-category",
              }}
            >
              <option value='None'>None</option>
              {/* <option value='State'>State</option> */}
              <option value='districts'>District</option>
              <option value='cong'>Congressional District</option>
              <option value='rep'>Representative District</option>
              <option value='counties'>County</option>
              <option value='mpos'>MPO</option>
              <option value='regions'>Region</option>
            </NativeSelect>
          </FormControl>
        }
        <FormControl fullWidth>
          <InputLabel htmlFor="select-layer">
            {pathname === '/mpo' ? 'MPO' : 'Layer'}:
          </InputLabel>
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label='select-layer' />}
            value={layer}
            onChange={(e) => {
              setLayer(e.target.value)
            }}
            inputProps={{
              name: 'layer',
              id: 'select-layer'
            }}
          >
            <option value='None'>None</option>
            {(['counties', 'mpos', 'districts', 'regions'].includes(category) && layerNames) &&
              Object.entries(layerNames[category]).map(
                c => <option key={c[1]} value={category === 'counties' ? c[1] : c[0]}>{c[1]}</option>
              )
            }
            { category === 'rep' && REP_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>) }
            { category === 'cong' && CONG_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>) }
          </NativeSelect>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel htmlFor="select-year">
            Year:
          </InputLabel>
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label='select-year' />}
            value={year}
            onChange={(e) => { setYear(e.target.value) }}
            inputProps={{
              name: 'year',
              id: 'select-year'
            }}
          >
            {yearsArray.length > 0
              ? yearsArray.map(y => <option key={y} value={y}>{y}</option>)
              : <> 
                <option value={'2020'}>2020</option>
                <option value={'2021'}>2021</option>
              </>
            }
          </NativeSelect>
        </FormControl>
        {/* <FormControl fullWidth>
          <InputLabel htmlFor="select-vehicle-type">
            Vehicle Type:
          </InputLabel>
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label='select-vehicle-type' />}
            defaultValue={'AllVehicles'}
            inputProps={{
              name: 'vehicle-type',
              id: 'select-vehicle-type'
            }}
          >
            <option value={'AllVehicles'}>All vehicles</option>
            <option value={'TrucksOnly'}>Trucks only</option>
          </NativeSelect>
        </FormControl> */}
        <FormControl fullWidth>
          <InputLabel htmlFor="select-map-focus">
            Map Focus:
          </InputLabel>
          {/* 'None' is used for the value.
          An empty string does not display NativeSelect correctly. */}
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label="select-map-focus" />}
            value={mapFocus}
            onChange={(e) => { setMapFocus(e.target.value) }}
            inputProps={{
              name: 'map-focus',
              id: 'select-map-focus'
            }}
          >
            <option value={'None'}>None</option>
            {FOCUS_COLS.map(c => <option key={c.accessorKey} value={c.accessorKey}>{c.header}</option>)}
          </NativeSelect>
        </FormControl>
        <FiltersOptions labelFilters={labelFilters} setLabelFilters={setLabelFilters} />
        {/* <FormControl fullWidth>
          <InputLabel htmlFor="select-data-view">
            Data View:
          </InputLabel>
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label="select-data-view" />}
            defaultValue={'Summary'}
            inputProps={{
              name: 'data-view',
              id: 'select-data-view'
            }}
          >
            <option value={'Summary'}>Summary</option>
          </NativeSelect>
        </FormControl> */}
        {/* <FormControl fullWidth>
          <InputLabel htmlFor="select-report">
            Report:
          </InputLabel>
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label="select-report" />}
            defaultValue={'Delay'}
            inputProps={{
              name: 'report',
              id: 'select-report'
            }}
          >
            <option value={'Delay'}>Delay</option>
          </NativeSelect>
        </FormControl> */}
      </Stack>
      <main ref={containerRef} style={{ display: 'flex' }}>
        <TableTabs
          data={filteredMapData}
          mapRef={mapRef}
          geoLayerRef={geoLayerRef}
          fromToTable={fromToTable}
          setFromToTable={setFromToTable}
          tableTabValue={tableTabValue}
          setTableTabValue={setTableTabValue}
          tableWidth={TOTAL_WIDTH - mapWidth}
          showOnMapData={showOnMapData}
          setShowOnMapData={setShowOnMapData}
          showOnMapMonitoredData={showOnMapMonitoredData}
          setShowOnMapMonitoredData={setShowOnMapMonitoredData}
          pagination={pagination}
          setPagination={setPagination}
          category={category}
          year={year}
          layer={layer}
        />
        <div
          className='roads-resize-handle'
          onMouseDown={handleMouseDown}
        >
          <DragHandleIcon id='drag-handle-icon' sx={{ rotate: '90deg' }} />
        </div>
        {isResizing && <div ref={resizeRef} />}
        <RoadsMap
          mapData={filteredMapData}
          mapRef={mapRef}
          geoLayerRef={geoLayerRef}
          setFromToTable={setFromToTable}
          tableTabValue={tableTabValue}
          setTableTabValue={setTableTabValue}
          mapWidth={mapWidth}
          mapFocus={mapFocus}
          showOnMapData={showOnMapData}
          setShowOnMapData={setShowOnMapData}
          showOnMapMonitoredData={showOnMapMonitoredData}
          setShowOnMapMonitoredData={setShowOnMapMonitoredData}
          shouldFitMapLayerBounds={shouldFitMapLayerBounds}
          setShouldFitMapLayerBounds={setShouldFitMapLayerBounds}
          layerOption={layer}
          year={year}
        />
      </main>
      <TutorialPopper openTutorial={openTutorial} setOpenTutorial={setOpenTutorial} setTableTabValue={setTableTabValue} />
    </div>
  );
}